import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { DateField, PageHeader } from '@routes-plan/ui';
import { StandardModalBody, StandardModalFooter } from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import FormActions from '../shared/FormActions';
import LocationPicker, { isLocationComplete, toApiLocation } from '../shared/LocationPicker';
import {
  CatalogItem,
  INVENTORY_ROUTES,
  OfficeOption,
  ShowAlerts,
  apiErrorMessage,
  fetchCatalog,
  fetchOffices,
  formatMoney,
  newRequestId,
} from '../shared/inventoryApi';
import { ActionIcon, CompanyMoneyField, FieldGrid, IntField, PageBody, SaveButton, Section, fullRow, useInventoryAlert } from '../shared/ui';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

type Line = { key: number; productId: number | ''; quantity: string; unitCost: string; lotCode: string; expirationDate: string };

const emptyLine = (key: number): Line => ({ key, productId: '', quantity: '', unitCost: '', lotCode: '', expirationDate: '' });

/** Registro de entrada de mercancía con destino sede o ruta. */
interface IncomeAddProps extends ShowAlerts {
  /** 'modal': dentro de StandardModal (composed), p. ej. desde Productos por ruta. */
  variant?: 'page' | 'modal';
  onClose?: () => void;
  onSaved?: () => void;
  /** Destino preseleccionado (sede y, si aplica, ruta). */
  initialLocation?: { officeId: number | null; routeId: number | null };
  /** Producto preseleccionado en la primera línea. */
  initialProductId?: number;
}

const StockReceiptNew: React.FC<IncomeAddProps> = ({
  showLoading,
  hideLoading,
  variant = 'page',
  onClose,
  onSaved,
  initialLocation,
  initialProductId,
}) => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const navigate = useNavigate();
  const inModal = variant === 'modal';
  const { canCreate } = useResourcePermissions(INVENTORY_ROUTES.incomes);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [location, setLocation] = useState<{ officeId: number | null; routeId: number | null }>(
    initialLocation ?? { officeId: null, routeId: null },
  );
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [date, setDate] = useState(nowOperationalDateString());
  const [invoice, setInvoice] = useState('');
  const [provider, setProvider] = useState('');
  const [observation, setObservation] = useState('');
  const [lines, setLines] = useState<Line[]>([{ ...emptyLine(1), productId: initialProductId ?? '' }]);
  const [errors, setErrors] = useState<{ location?: string; lines?: string }>({});
  const [saving, setSaving] = useState(false);
  const requestId = useRef(newRequestId('income'));
  if (!inModal) setPageTitle('inventory.receipts.newPageTitle');

  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);

  useEffect(() => {
    if (!isLocationComplete(location)) {
      setCatalog([]);
      return;
    }
    fetchCatalog(toApiLocation(location), { includeUnassigned: true })
      .then(setCatalog)
      .catch((error) => {
        setCatalog([]);
        showAlert(apiErrorMessage(error, t('inventory.common.productsLoadError')), 'error');
      });
  }, [location, showAlert, t]);

  const productById = useMemo(() => new Map(catalog.map((c) => [c.productId, c])), [catalog]);
  const total = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0), 0);

  const updateLine = (key: number, patch: Partial<Line>) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!canCreate) return;
    const nextErrors: { location?: string; lines?: string } = {};
    if (!isLocationComplete(location)) nextErrors.location = t('inventory.receipts.selectDestination');
    const items = lines.filter((l) => l.productId !== '');
    if (!items.length || items.some((l) => !(Number(l.quantity) > 0))) nextErrors.lines = t('inventory.common.addLineWarning');
    setErrors(nextErrors);
    if (nextErrors.location || nextErrors.lines) return;
    setSaving(true);
    showLoading();
    try {
      const { data: saved } = await axios.post('stock-receipts', {
        ...toApiLocation(location),
        date,
        invoice: invoice || null,
        provider: provider || null,
        observation: observation || null,
        clientRequestId: requestId.current,
        items: items.map((l) => ({
          productId: l.productId,
          quantity: Number(l.quantity),
          unitCost: Number(l.unitCost) || 0,
          lotCode: l.lotCode || null,
          expirationDate: l.expirationDate || null,
        })),
      });
      const enabledCount = Array.isArray(saved?.autoEnabledProductIds) ? saved.autoEnabledProductIds.length : 0;
      showAlert(
        enabledCount
          ? t(enabledCount === 1 ? 'inventory.receipts.savedOne' : 'inventory.receipts.savedMany', { n: enabledCount })
          : t('inventory.receipts.saved'),
        'success',
      );
      if (inModal) onSaved?.();
      else navigate(INVENTORY_ROUTES.incomes);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.receipts.saveError')), 'error');
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const destinationSection = (
    <Section plain={inModal} title={t('inventory.receipts.sectionDestination')} description={t('inventory.receipts.destinationHelp')} tour="receipt-new-destination">
      <Box sx={{ display: 'grid', gap: 2 }}>
        <LocationPicker
          offices={offices}
          value={location}
          onChange={(v) => {
            setLocation(v);
            setErrors((e) => ({ ...e, location: undefined }));
          }}
          disabled={!canCreate}
          size="medium"
          tourPrefix="receipt-new"
        />
        {errors.location && (
          <Typography variant="caption" color="error" role="alert" sx={{ mt: -1 }}>
            {errors.location}
          </Typography>
        )}
        <FieldGrid md={3} data-tour="receipt-new-data">
          <DateField fullWidth label={t('common.date')} value={date || null} onChange={(v) => setDate(v ?? '')} disabled={!canCreate} />
          <TextField fullWidth label={t('inventory.common.invoice')} value={invoice} onChange={(e) => setInvoice(e.target.value)} disabled={!canCreate} />
          <TextField fullWidth label={t('inventory.common.supplier')} value={provider} onChange={(e) => setProvider(e.target.value)} disabled={!canCreate} />
        </FieldGrid>
      </Box>
    </Section>
  );

  const noCatalog = isLocationComplete(location) && catalog.length === 0;
  const productsSection = (
    <Section
      plain={inModal}
      tour="receipt-new-products"
      title={t('inventory.common.products')}
      description={noCatalog ? t('inventory.receipts.noCatalog') : !isLocationComplete(location) ? t('inventory.receipts.chooseDestinationFirst') : undefined}
    >
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {lines.map((line, index) => {
          const product = line.productId !== '' ? productById.get(Number(line.productId)) : undefined;
          const subtotal = (Number(line.quantity) || 0) * (Number(line.unitCost) || 0);
          return (
            <Paper
              key={line.key}
              data-tour="receipt-new-line"
              variant="outlined"
              sx={{
                p: 1.5,
                display: 'grid',
                gap: 1.5,
                alignItems: 'start',
                bgcolor: 'action.hover',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'minmax(0, 3fr) minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1.5fr) auto',
                },
              }}
            >
              <TextField
                select
                fullWidth
                size="small"
                label={t('inventory.common.productN', { n: index + 1 })}
                value={line.productId === '' ? '' : String(line.productId)}
                onChange={(e) => updateLine(line.key, { productId: Number(e.target.value) || '' })}
                disabled={!canCreate || catalog.length === 0}
                sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
              >
                {catalog.map((c) => (
                  <MenuItem key={c.productId} value={String(c.productId)}>
                    {c.title}
                    {c.sku ? ` · ${c.sku}` : ''}
                    {c.willEnable ? ` ${t('inventory.receipts.willEnable')}` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <IntField size="small" fullWidth label={t('inventory.common.quantity')} value={line.quantity} onChange={(v) => updateLine(line.key, { quantity: v })} disabled={!canCreate} />
              <CompanyMoneyField
                size="small"
                fullWidth
                label={t('inventory.common.unitCost')}
                data-tour="receipt-new-cost"
                value={line.unitCost}
                onChange={(v) => updateLine(line.key, { unitCost: v })}
                disabled={!canCreate}
                helperText={subtotal > 0 ? t('inventory.common.subtotalValue', { value: formatMoney(subtotal) }) : undefined}
              />
              <TextField size="small" fullWidth label={t('inventory.common.batch')} value={line.lotCode} onChange={(e) => updateLine(line.key, { lotCode: e.target.value })} disabled={!canCreate} />
              <DateField
                size="small"
                fullWidth
                label={t('inventory.common.expires')}
                value={line.expirationDate || null}
                onChange={(v) => updateLine(line.key, { expirationDate: v ?? '' })}
                disabled={!canCreate || !product?.hasExpiration}
                helperText={product?.hasExpiration ? undefined : t('inventory.common.notApplicable')}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 40, gridColumn: { xs: '2', md: 'auto' } }}>
                <ActionIcon
                  label={t('inventory.common.removeLine')}
                  icon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                  color="error"
                  disabled={!canCreate || lines.length <= 1}
                  onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== line.key) : prev))}
                />
              </Box>
            </Paper>
          );
        })}
        {errors.lines && (
          <Typography variant="caption" color="error" role="alert">
            {errors.lines}
          </Typography>
        )}
        <Box data-tour="receipt-new-add" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<AddOutlinedIcon />}
            onClick={() => setLines((prev) => [...prev, emptyLine(Math.max(...prev.map((l) => l.key)) + 1)])}
            disabled={!canCreate}
          >
            {t('inventory.common.addProduct')}
          </Button>
          <Typography variant="subtitle1" sx={{ ml: 'auto', fontWeight: 600 }}>
            {t('inventory.common.totalCostValue', { value: formatMoney(total) })}
          </Typography>
        </Box>
        <FieldGrid md={1}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('inventory.common.observation')}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            disabled={!canCreate}
            sx={fullRow}
          />
        </FieldGrid>
      </Box>
    </Section>
  );

  if (inModal) {
    return (
      <>
        <StandardModalBody>
          {destinationSection}
          <Divider sx={{ my: 3 }} />
          {productsSection}
        </StandardModalBody>
        <StandardModalFooter>
          <Button color="inherit" variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <SaveButton allowed={canCreate} deniedTooltip={t('inventory.receipts.noPermissionSave')} loading={saving} onClick={save}>
            {t('common.save')}
          </SaveButton>
        </StandardModalFooter>
      </>
    );
  }

  return (
    <PageBody>
      <PageHeader
        title={t('inventory.receipts.create')}
        helpAction={<ScreenTourButton />}
        breadcrumbs={[{ label: t('inventory.receipts.title'), onClick: () => navigate(INVENTORY_ROUTES.incomes) }, { label: t('inventory.receipts.create') }]}
      />
      <Box sx={{ display: 'grid', gap: 2, maxWidth: 1200, width: '100%' }}>
        {destinationSection}
        {productsSection}
        <FormActions allowed={canCreate} deniedTooltip={t('inventory.receipts.noPermissionSave')} onSave={save} cancelTo={INVENTORY_ROUTES.incomes} loading={saving} tour="receipt-new-save" />
      </Box>
    </PageBody>
  );
};

export default StockReceiptNew;
